import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import * as Icons from "lucide-react";
import { images } from "../mock";

export const DataSecurity = () => {
  const securityFeatures = [
    {
      title: "DSGVO-konforme Datenlöschung",
      description: "Vollständige Einhaltung aller Datenschutzrichtlinien und gesetzlicher Vorgaben",
      icon: "ShieldCheck",
    },
    {
      title: "Zertifizierte Verfahren",
      description: "Einsatz zertifizierter Löschverfahren nach internationalen Standards",
      icon: "Award",
    },
    {
      title: "Löschzertifikat pro Gerät",
      description: "Individuelles Löschzertifikat für jedes einzelne Gerät zur Dokumentation",
      icon: "FileCheck",
    },
    {
      title: "Physische Datenträger-Zerstörung",
      description: "Optional: Mechanische Zerstörung der Festplatten und SSDs",
      icon: "HardDrive",
    },
  ];

  const process = [
    {
      step: "1",
      title: "Geräteerfassung",
      description: "Vollständige Dokumentation aller zu löschenden Geräte",
    },
    {
      step: "2",
      title: "Professionelle Datenlöschung",
      description: "Mehrfaches Überschreiben nach zertifizierten Standards",
    },
    {
      step: "3",
      title: "Verifikation",
      description: "Überprüfung der vollständigen Datenlöschung",
    },
    {
      step: "4",
      title: "Zertifizierung",
      description: "Ausstellung individueller Löschzertifikate",
    },
  ];

  const compliance = [
    {
      title: "DSGVO-Konformität",
      description: "Vollständige Einhaltung der EU-Datenschutz-Grundverordnung",
      icon: "Scale",
    },
    {
      title: "ISO-Zertifiziert",
      description: "Zertifizierte Prozesse nach internationalen Standards",
      icon: "Award",
    },
    {
      title: "Dokumentationspflicht",
      description: "Lückenlose Dokumentation für Ihre Compliance",
      icon: "FileText",
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Datenlöschung & Sicherheit
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Professionelle und DSGVO-konforme Datenlöschung für Ihre Firmenlaptops. 
              Wir garantieren sichere Datenlöschung mit individuellen Löschzertifikaten 
              für jedes Gerät.
            </p>
          </div>
        </div>
      </section>

      {/* Security Image */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <img
              src={images.securityShield}
              alt="Data Security"
              className="w-full h-96 object-cover rounded-2xl shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Security Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Höchste Sicherheitsstandards
            </h2>
            <p className="text-xl text-gray-600">
              Zertifizierte Datenlöschung für Ihre Sicherheit
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {securityFeatures.map((feature, index) => {
              const Icon = Icons[feature.icon];
              return (
                <Card key={index} className="border-blue-100 hover:border-blue-300 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="text-blue-600" size={28} />
                    </div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-lg">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Datenlöschungs-Prozess
            </h2>
            <p className="text-xl text-gray-600">
              Sichere und dokumentierte Datenlöschung in 4 Schritten
            </p>
          </div>
          <div className="max-w-3xl mx-auto">
            {process.map((step, index) => (
              <Card key={index} className="mb-6 last:mb-0 border-blue-100 hover:shadow-md transition-all duration-300">
                <CardContent className="p-8 flex items-start space-x-6">
                  <div className="w-14 h-14 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold flex-shrink-0">
                    {step.step}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 text-lg">
                      {step.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-20 bg-blue-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Compliance & Zertifizierungen
            </h2>
            <p className="text-xl text-gray-600">
              Erfüllung aller gesetzlichen und regulatorischen Anforderungen
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {compliance.map((item, index) => {
              const Icon = Icons[item.icon];
              return (
                <Card key={index} className="border-blue-200 hover:border-blue-300 hover:shadow-md transition-all duration-300">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="text-blue-600" size={32} />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {item.title}
                    </h3>
                    <p className="text-gray-600">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Card className="border-blue-200 shadow-lg">
              <CardContent className="p-12">
                <div className="flex items-start space-x-6">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icons.Lock className="text-white" size={32} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      Ihre Daten sind bei uns sicher
                    </h3>
                    <p className="text-gray-600 text-lg leading-relaxed mb-4">
                      Wir verstehen die Bedeutung von Datensicherheit für Ihr Unternehmen. 
                      Deshalb setzen wir ausschließlich zertifizierte Verfahren ein und 
                      dokumentieren jeden Schritt lückenlos.
                    </p>
                    <p className="text-gray-600 text-lg leading-relaxed">
                      Mit unseren individuellen Löschzertifikaten haben Sie jederzeit den 
                      Nachweis über die ordnungsgemäße und DSGVO-konforme Datenlöschung.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Sichere Datenlöschung gewünscht?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Fordern Sie jetzt Ihr Angebot mit zertifizierter Datenlöschung an
            </p>
            <Link to="/request-quote">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 font-medium shadow-lg">
                Jetzt Angebot anfragen
                <Icons.ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
